import { Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import {
  PortalDsBadge,
  PortalDsButton,
  PortalDsEmptyState,
  portalDs,
} from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import { useActiveClient } from 'src/redux/active-client/use-active-client';
import { ClientUser } from '../../types/client.types';
import {
  useDeleteClientUserMutation,
  useGetOrganizationClientsQuery,
  useLazyGetClientInviteCodeQuery,
} from '../../endpoints/client.endpoints';
import notificationService from 'src/services/notification/notification.service';
import { useDispatch } from 'react-redux';
import { ClientAvatar } from './client-avatar';
import { getUserInitials } from '../../utils/client-display.utils';

interface ManageClientUsersProps {
  showAddUser: boolean;
  showInviteUser: boolean;
  onInviteUser: (show: boolean) => void;
  onAddUser: (show: boolean) => void;
}

const ManageClientUsers: React.FC<ManageClientUsersProps> = () => {
  const { activeClient, setClient } = useActiveClient();
  const users = activeClient?.users ?? [];
  const [isCopyingInvite, setIsCopyingInvite] = useState(false);
  const dispatch = useDispatch();

  const [deleteClientUser] = useDeleteClientUserMutation();
  const [getClientInviteCode] = useLazyGetClientInviteCodeQuery();
  const { refetch: refetchClients } = useGetOrganizationClientsQuery();

  const getStatus = (user: ClientUser) => {
    if (user.pending) {
      return { label: 'Pending', tone: 'paused' as const };
    }
    return { label: 'Active', tone: 'complete' as const };
  };

  const getRole = (user: ClientUser) => {
    const groups = user.user?.permissions?.groups ?? [];
    if (groups.includes('client_admin')) {
      return 'Admin';
    }
    return 'User';
  };

  const refreshClient = async() => {
    const result = await refetchClients();
    const next = result.data?.find((client) => client.ext_client_id === activeClient?.ext_client_id);
    if (next) {
      setClient(next);
    }
  };

  const copyInviteLink = async() => {
    if (!activeClient?.ext_client_id) {
      notificationService.handleError(null, dispatch, 'No client selected');
      return;
    }

    try {
      setIsCopyingInvite(true);
      const inviteCodeResponse = await getClientInviteCode({
        clientId: activeClient.ext_client_id,
        forAdmin: false,
      }).unwrap();
      const inviteLink = `${window.location.origin}/create-account?invite_code=${inviteCodeResponse.invite_code}&registration_type=client`;

      await navigator.clipboard.writeText(inviteLink);
      notificationService.handleSuccess(null, dispatch, 'Invite link copied');
    } catch (_error) {
      notificationService.handleError(null, dispatch, 'Unable to copy invite link');
    } finally {
      setIsCopyingInvite(false);
    }
  };

  const handleDelete = async(userId: string) => {
    const response = await deleteClientUser({
      clientUserId: userId,
      clientId: activeClient.ext_client_id,
    });
    if (!('data' in response)) {
      notificationService.handleError(null, dispatch, 'Error deleting user');
      return;
    }
    notificationService.handleSuccess(null, dispatch, 'User deleted');
    await refreshClient();
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-xl border border-[#EDEEF2] bg-[#F7F8FA] px-5 py-4">
        <div className="mb-0.5 text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]">
          Create invite link
        </div>
        <p className={cn(portalDs.caption, 'mb-3')}>
          Copy a signup link for this client and send it manually. No email is sent from the platform.
        </p>
        <div className="flex">
          <PortalDsButton
            variant="primary"
            disabled={isCopyingInvite}
            onClick={() => void copyInviteLink()}
          >
            {isCopyingInvite ? 'Copying...' : 'Copy invite link'}
          </PortalDsButton>
        </div>
      </div>

      {users.length === 0 ? (
        <PortalDsEmptyState
          title="No users yet"
          description="Invite someone from this client to give them access to their reporting."
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-[#EDEEF2]">
          <div className="grid grid-cols-[44px_minmax(0,2fr)_1fr_1fr_40px] gap-4 border-b border-[#EDEEF2] bg-[#F7F8FA] px-5 py-3">
            <div />
            <div className={portalDs.tableHeadCell}>
              Email
            </div>
            <div className={portalDs.tableHeadCell}>
              Role
            </div>
            <div className={portalDs.tableHeadCell}>
              Status
            </div>
            <div />
          </div>
          {users.map((user) => {
            const status = getStatus(user);
            const role = getRole(user);
            return (
              <div
                key={user.id}
                className="grid grid-cols-[44px_minmax(0,2fr)_1fr_1fr_40px] items-center gap-4 border-b border-[#EDEEF2] px-5 py-3 last:border-b-0 hover:bg-[#F0F1F5]"
              >
                <ClientAvatar
                  title={user.email}
                  initials={getUserInitials(user.email)}
                  size={32}
                />
                <div className="truncate text-[13.5px] font-medium text-[#0D0F1A]">
                  {user.email}
                </div>
                <div>
                  <PortalDsBadge variant={role === 'Admin' ? 'blue' : 'gray'}>
                    {role}
                  </PortalDsBadge>
                </div>
                <div className="flex items-center gap-1.5 text-[13px] font-medium text-[#0D0F1A]">
                  <span
                    className={cn(
                      'size-[7px] rounded-full',
                      status.tone === 'complete' ? 'bg-[#059669]' : 'bg-[#D97706]'
                    )}
                  />
                  {status.label}
                </div>
                <PortalDsButton
                  variant="ghost"
                  size="sm"
                  className="size-7 p-0"
                  aria-label={`Remove ${user.email}`}
                  onClick={() => void handleDelete(user.id)}
                >
                  <Trash2 className="size-3.5" />
                </PortalDsButton>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ManageClientUsers;
